/* eslint-disable no-console */
import {
	default as React,
	useEffect,
	useState,
	useReducer,
	useRef,
	useCallback,
	useMemo
} from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
	TextField,
	Button,
	FormControlLabel,
	Select,
	MenuItem,
	Tooltip,
	IconButton,
	Snackbar,
	CircularProgress,
	Typography,
	Box,
	FormControl,
	InputLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Chip,
	Collapse,
	Switch
} from "@mui/material";
import { Star, StarOutline, Delete, Close, ArrowBack, ExpandMore, Edit } from "@mui/icons-material";
import PlacesAutocomplete, {
	geocodeByAddress,
	geocodeByPlaceId,
	getLatLng
} from "react-places-autocomplete";

import {
	useViewedBar,
	asyncGetBarImages,
	useBarUtils
} from "../actions/bars";
import {
	useCategories,
	useFetchCategories
} from "../actions/categories";
import { useUserRoles } from "../globalstore";
import { useSetHeaderTitle } from "../actions/headerContext";
import { THEME_MAP } from "../theme";
import { auth, firestore, functions } from "../services/firebase";
import { useStateRegions, useStateRegionActions } from "../actions/stateRegions";
import OpeningHoursForm from "../components/OpeningHoursForm";
import {
	NAME_REQUIRED_TEXT,
	PHONE_NUMBER_VALID_TEXT,
	PHONE_REGEX,
	SELECT_ADDRESS_TEXT,
	WEBSITE_URL_REGEX,
	WEBSITE_VALID_TEXT
} from "../constants";
import { useUserUtils } from "../actions/users";
import { captureError } from "../lib/errorReporting";
import firebase from "firebase";
import {
	isTimeRangeComplete,
	getTotalMinutes,
	MINUTES_IN_DAY,
	normalizeDayHours
} from "../lib/hours";
import {
	CATEGORY_DAYS,
	createEmptyCategoryState,
	cloneCategoryState,
	normalizeCategoryState,
	areDaysUniform,
	replicateStateFromDay,
	formatDayLabel,
	getDayDifferences,
	getUniformSubcategories
} from "../lib/categories";
import { UPDATED_SORTING_COLLECTION } from "../actions/bars";
import { parseAddressParts, normalizeWhitespace } from "../lib/address";

const DAYS = [
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
	"sunday"
];

const createEmptyDayHours = () => ({
	isOpen: false,
	openTime: null,
	closeTime: null
});

const createDefaultOpeningHours = () => {
	const hours = {};
	DAYS.forEach((day) => {
		hours[day] = createEmptyDayHours();
	});
	return hours;
};

const cloneHours = (hours) => JSON.parse(JSON.stringify(hours || {}));

const hoursHaveOpenDay = (hours) =>
	DAYS.some((day) => Boolean(hours?.[day]?.isOpen));

const parseMaybeNumber = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const toSafeString = (value) =>
	value === null || value === undefined ? "" : String(value);

const normalizeAddressField = (value = "") => normalizeWhitespace(String(value || ""));

// Title-case a bar name for consistent storage/display. Intentional mixed-case
// brand names (e.g. "McMenamins", "BJ's") are preserved — only all-lowercase or
// all-uppercase input is reformatted.
const formatBarName = (value = "") => {
	const trimmed = normalizeWhitespace(String(value || ""));
	if (!trimmed) return trimmed;
	const isAllLower = trimmed === trimmed.toLowerCase();
	const isAllUpper = trimmed === trimmed.toUpperCase();
	if (!isAllLower && !isAllUpper) return trimmed;
	return trimmed
		.toLowerCase()
		.split(" ")
		.map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
		.join(" ");
};

const buildFullAddress = ({ addressLine, city, state, postalCode }) => {
	const parts = [addressLine, city, state, postalCode]
		.map(normalizeAddressField)
		.filter(Boolean);
	return parts.join(", ");
};

// Pull the fields we validate against from a Google geocoder result so we can
// reject matches that landed in the wrong place (e.g. an ambiguous highway
// address snapping to a far-off city — the "Park Shore / US-31" bug).
const extractGeocodeParts = (result) => {
	const components = result?.address_components || [];
	const findComponent = (type) =>
		components.find((c) => (c.types || []).includes(type));
	return {
		zip: findComponent("postal_code")?.long_name || "",
		city:
			findComponent("locality")?.long_name ||
			findComponent("postal_town")?.long_name ||
			findComponent("sublocality")?.long_name ||
			"",
		state: findComponent("administrative_area_level_1")?.short_name || "",
		locationType: result?.geometry?.location_type || "",
		types: result?.types || [],
		formattedAddress: result?.formatted_address || ""
	};
};

const normalizePhoneNumber = (value = "") => {
	const digits = String(value || "").replace(/\D/g, "");
	if (digits.length < 10) {
		return value;
	}
	const normalized = digits.slice(-10);
	return `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}-${normalized.slice(6)}`;
};

const buildHoursFromSource = (source) => {
	const hours = createDefaultOpeningHours();

	if (!source || typeof source !== "object") {
		return hours;
	}

	if (source.parsed !== undefined) {
		hours.parsed = Boolean(source.parsed);
	}

	if (typeof source.raw_opening_hours === "string") {
		hours.raw_opening_hours = source.raw_opening_hours;
	}

	DAYS.forEach((day) => {
		const value = source[day] || {};
		hours[day] = normalizeDayHours(value);
	});

	return hours;
};

const createEmptyBarState = () => ({
	barName: "",
	barId: "",
	barEmail: "",
	ownerEmail: "",
	barPhone: "",
	barUrl: "",
	hours: createDefaultOpeningHours(),
	barAddress: "",
	address_2: "",
	city: "",
	state: "",
	postalCode: "",

	barCoverImage: "",
	barImages: [],
	barDescription: "",
	liveUrl: "",

	barOpenDays: "",
	barClosingHours: "",
	barOpeningHours: "",
	hasChanged: false,
	latitude: null,
	longitude: null,

	position: {
		geopoint: {
			latitude: null,
			longitude: null
		}
	}
});

export function BarDetail() {
	const history = useHistory();
	const location = useLocation();
	const { id: routeId } = useParams();
	const id = routeId || "new";
	const isNew = id === "new";
	const {
		userRoles: { isAdmin }
	} = useUserRoles();

	const barSnapshot = useViewedBar();
	const { fetchBarById } = useBarUtils();

	const prefillBar = useMemo(() => {
		if (!isNew) return null;
		const params = new URLSearchParams(location.search || "");
		const prefillName =
			location.state?.prefillBarName ||
			location.state?.barName ||
			params.get("name") ||
			params.get("barName") ||
			"";
		const prefillAddress =
			location.state?.prefillBarAddress ||
			location.state?.barAddress ||
			params.get("address") ||
			params.get("barAddress") ||
			"";
		const prefillLatitude = parseMaybeNumber(
			location.state?.prefillLatitude ??
			location.state?.latitude ??
			params.get("lat") ??
			params.get("latitude")
		);
		const prefillLongitude = parseMaybeNumber(
			location.state?.prefillLongitude ??
			location.state?.longitude ??
			params.get("lng") ??
			params.get("lon") ??
			params.get("long") ??
			params.get("longitude")
		);
		const prefillDataSource =
			location.state?.prefillDataSource ||
			location.state?.dataSource ||
			params.get("dataSource") ||
			params.get("data_source") ||
			"";

		const shouldPrefill =
			Boolean(prefillName) ||
			Boolean(prefillAddress) ||
			prefillLatitude !== null ||
			prefillLongitude !== null ||
			Boolean(prefillDataSource);

		const base = createEmptyBarState();
		if (!shouldPrefill) return base;

		const next = {
			...base,
			barName: prefillName,
			barAddress: prefillAddress
		};

		if (prefillLatitude !== null && prefillLongitude !== null) {
			next.latitude = prefillLatitude;
			next.longitude = prefillLongitude;
			next.position = {
				geopoint: {
					latitude: prefillLatitude,
					longitude: prefillLongitude
				}
			};
		}

		if (prefillDataSource) {
			next.dataSource = prefillDataSource;
		}

		return next;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNew, location.state, location.search]);

	const [bar, setBar] = useState(() => prefillBar);
	const [isFetching, setIsFetching] = useState();
	const [imgUrls, setImgUrls] = useState([]);

	const [isOpen, setIsOpen] = useState(false);
	const [note, setNote] = useState("");

	const [barInfoDirty, setBarInfoDirty] = useState(false);
	const [categoryDirty, setCategoryDirty] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState(null);

	const barInfoSaveRef = useRef(null);
	const categorySaveRef = useRef(null);
	// Set true while an intentional save is navigating (new bar redirect), so the
	// unsaved-changes guard doesn't intercept the save's own navigation.
	const savingRef = useRef(false);

	const isDirty = barInfoDirty || categoryDirty;

	useEffect(() => {
		if (!isDirty) return;
		const unblock = history.block((location, action) => {
			// Allow navigation triggered by an in-progress save (e.g. the new-bar
			// redirect); only prompt for genuine "leave with unsaved changes".
			if (savingRef.current) return undefined;
			setPendingNavigation({ location, action });
			return false;
		});
		return unblock;
	}, [isDirty, history]);

	const handleLeaveAnyway = () => {
		const { location, action } = pendingNavigation;
		setPendingNavigation(null);
		// Temporarily unblock so navigation goes through
		const unblock = history.block(() => {});
		if (action === "POP") {
			history.goBack();
		} else if (action === "REPLACE") {
			history.replace(location);
		} else {
			history.push(location);
		}
		unblock();
	};

	const handleStayAndSave = async () => {
		setPendingNavigation(null);
		if (barInfoDirty && barInfoSaveRef.current) {
			await barInfoSaveRef.current();
		}
		if (categoryDirty && categorySaveRef.current) {
			await categorySaveRef.current();
		}
	};

	const setHeaderTitle = useSetHeaderTitle();

	const closeNote = (_event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setNote("");
		setIsOpen(false);
	};

	const openNote = (message) => {
		setNote(message);
		setIsOpen(true);
	};

	useEffect(() => {
		// fetch the images if we have a bar, or fetch the bar if we don't have it yet
		if (barSnapshot && barSnapshot.id === id) {
			let _bar = barSnapshot.barName
				? barSnapshot
				: barSnapshot.data();
			setBar(_bar);
			setImgUrls([]);
			if (_bar?.barImages) {
				asyncGetBarImages(id, _bar).then((res) => {
					if (!res || res.length < 1) {
						return;
					}

					setImgUrls([...res]);
				}).catch((err) => {
					captureError(err, { view: "BarDetail", action: "asyncGetBarImages" });
					console.error("Failed to load bar images", err);
				});
			}
			return;
		}

		if (id && id !== "new") {
			setBar(null);
			setIsFetching(true);
			fetchBarById(id).then(() => setIsFetching(false)).catch((err) => {
				captureError(err, { view: "BarDetail", action: "fetchBarById" });
				console.error("Failed to fetch bar", err);
				setIsFetching(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [barSnapshot, id]);

	useEffect(() => {
		setHeaderTitle(id === "new" && "Create new bar");
	}, [id, setHeaderTitle]);

	useEffect(() => {
		if (!isNew) return;
		setBar(prefillBar);
	}, [isNew, prefillBar]);

	if (isFetching)
		return (
			<Container className={"is-loading"}>
				<LoadingFog className={"loading-fog is-loading"}>
					<CircularProgress color="primary" />
				</LoadingFog>
			</Container>
		);

	return (
		<Container>
			<BackButton onClick={() => history.goBack()}>
				<ArrowBack fontSize="small" />
				Back
			</BackButton>
			<div className="bar-actions">
				{isAdmin && !isNew && (
					<Button
						onClick={() => history.push(`/managers/${id}`)}
					>
						Edit Managers
					</Button>
				)}
			</div>
			<BarInfoSection bar={bar} openNote={openNote} onDirtyChange={setBarInfoDirty} saveRef={barInfoSaveRef} savingRef={savingRef} />
			<BarImgSection
				bar={bar}
				barImages={imgUrls}
				openNote={openNote}
			/>
			<CategorySection bar={bar} openNote={openNote} onDirtyChange={setCategoryDirty} saveRef={categorySaveRef} />
			<Snackbar
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				open={isOpen}
				autoHideDuration={3000}
				onClose={closeNote}
				message={note}
				action={
					<IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={closeNote}
					>
						<Close fontSize="small" />
					</IconButton>
				}
			/>
			<Dialog
				open={Boolean(pendingNavigation)}
				onClose={handleStayAndSave}
			>
				<DialogTitle>Unsaved Changes</DialogTitle>
				<DialogContent>
					<DialogContentText>
						You have unsaved changes. Would you like to save before leaving?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleLeaveAnyway} color="error">
						Leave Anyway
					</Button>
					<Button onClick={handleStayAndSave} variant="contained" color="primary">
						Save Now
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;

	padding: 24px;
	overflow: auto;
	position: relative;

	&.is-loading {
		overflow: hidden;
	}

	.bar-actions {
		width: 100%;
		margin: auto;
		display: flex;
		justify-content: flex-end;
	}

	@media (max-width: 767px) {
		padding: 16px;
	}
`;

const BackButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: none;
	border: none;
	cursor: pointer;
	color: ${THEME_MAP["color-primary-500"]};
	font-size: 0.9rem;
	padding: 4px 0;
	margin-bottom: 12px;

	&:hover {
		text-decoration: underline;
	}
`;

const LoadingFog = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: none;
	align-items: center;
	justify-content: center;
	background: rgba(255, 235, 235, 0.6);
	&.is-loading {
		display: flex;
	}
`;

const LoadingContainer = styled.div`
	width: 100%;
	margin: auto;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 8px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	min-height: 300px;
	padding: 16px 32px;
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

function BarImgSection({ barImages = [], bar, openNote }) {
	const { uploadImage, deleteImage, markAsBannerImage } =
		useBarUtils();
	const { id: barId } = useParams();
	const effectiveBarId = bar?.barId || (barId !== "new" ? barId : null);
	const canUpload = Boolean(effectiveBarId);

	const [bannerImg, setBannerImg] = useState(null);
	const fileSelectRef = useRef(null);
	const [isUploading, setIsUploading] = useState(false);

	useEffect(() => {
		setBannerImg(
			bar?.barCoverImage !== "" ? bar?.barCoverImage : null
		);
	}, [bar]);

	const clickFileUpload = (e) => {
		e.preventDefault();
		// eslint-disable-next-line no-unused-expressions
		fileSelectRef?.current.click();
	};

	const handleFileSelect = () => {
		if (!canUpload) {
			openNote("Save the bar before uploading images.");
			return;
		}
		setIsUploading(true);
		const file = fileSelectRef.current.files[0];
		resizeImage({
			file,
			maxSize: 600
		})
			.then(function (resizedImage) {
				uploadImage(barId, bar, resizedImage).then(
					() => {
						openNote("Image Uploaded successfully");
						setIsUploading(false);
					}
				);
			})
			.catch(function (err) {
				setIsUploading(false);
				openNote("Failed to upload Image");
				captureError(err || "Image upload failed", { action: "uploadBarImage" });
			});
	};

	const handleImageDelete = (url) => {
		setIsUploading(true);
		deleteImage(url, effectiveBarId, bar?.barImages, bar?.barCoverImage)
			.then(() => {
				openNote("Image deleted successfully");
				setIsUploading(false);
			})
			.catch((err) => {
				openNote("Image couldn't be deleted");
				setIsUploading(false);
				captureError(err || "Image delete failed", { action: "deleteBarImage" });
			});
	};

	const handleCoverImageSelect = (url, isBanner) => {
		if (!canUpload) {
			openNote("Save the bar before updating the banner image.");
			return;
		}
		setIsUploading(true);
		markAsBannerImage(url, isBanner, effectiveBarId)
			.then(() => {
				openNote("Banner Image Updated");
				setIsUploading(false);
			})
			.catch((err) => {
				openNote("Image couldn't be Updated");
				setIsUploading(false);
				captureError(err || "Banner image update failed", { action: "markAsBannerImage" });
			});
	};

	if (isUploading)
		return (
			<LoadingContainer>
				<CircularProgress />
			</LoadingContainer>
		);
	return (
		<BarImgContainer>
			{barImages.length > 0 ? (
				<div className="image-grid">
					{barImages.map((url) => {
						const isBanner = url?.includes(bannerImg);
						return (
							<div key={url} className={`image-card${isBanner ? " is-banner" : ""}`}>
								<img alt="" src={url} />
								{isBanner && (
									<span className="banner-badge">Banner</span>
								)}
								<div className="image-actions">
									<Tooltip title={isBanner ? "Remove as Banner" : "Set as Banner"}>
										<IconButton
											size="small"
											onClick={() => handleCoverImageSelect(url, isBanner)}
										>
											{isBanner ? <Star /> : <StarOutline />}
										</IconButton>
									</Tooltip>
									<Tooltip title="Delete Image">
										<IconButton
											size="small"
											onClick={() => handleImageDelete(url)}
										>
											<Delete />
										</IconButton>
									</Tooltip>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="no-image-holder">
					<Typography variant="body1" color="textSecondary">
						Upload an image so patrons can see you!
					</Typography>
				</div>
			)}
			<div className="section-footer">
				<Button
					color="primary"
					variant="outlined"
					onClick={clickFileUpload}
					disabled={!canUpload}
				>
					Upload Images
				</Button>
				<input
					type="file"
					accept="image/*"
					ref={fileSelectRef}
					onChange={handleFileSelect}
					disabled={!canUpload}
				/>
				{!canUpload && (
					<Typography variant="body2" color="textSecondary">
						Save the bar to enable image uploads.
					</Typography>
				)}
			</div>
		</BarImgContainer>
	);
}

const BarImgContainer = styled.div`
	width: 100%;
	margin: auto;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 4px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	padding: 16px;
	margin-bottom: 16px;

	.image-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
		gap: 12px;
	}

	.image-card {
		position: relative;
		border-radius: 8px;
		overflow: hidden;
		aspect-ratio: 1;
		background: #f5f5f5;
		border: 2px solid transparent;
		transition: border-color 0.2s;

		&.is-banner {
			border-color: #ffc107;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		.banner-badge {
			position: absolute;
			top: 8px;
			left: 8px;
			background: #ffc107;
			color: #000;
			font-size: 11px;
			font-weight: 600;
			padding: 2px 8px;
			border-radius: 4px;
			text-transform: uppercase;
			letter-spacing: 0.5px;
		}

		.image-actions {
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			display: flex;
			justify-content: space-between;
			padding: 6px 8px;
			background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
			opacity: 0;
			transition: opacity 0.2s;

			.MuiIconButton-root {
				color: #fff;
				&:hover { opacity: 0.8; }
			}
		}

		&:hover .image-actions {
			opacity: 1;
		}
	}

	.no-image-holder {
		padding: 48px 16px;
		text-align: center;
	}

	.section-footer {
		padding-top: 12px;
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 12px;
	}

	input[type="file"] {
		visibility: hidden;
		pointer-events: none;
		width: 1px;
		height: 1px;
		position: fixed;
		top: -10;
		left: -10;
	}
`;

function BarInfoSection({ bar, openNote, onDirtyChange, saveRef, savingRef }) {
	const viewedBar = useViewedBar();
	const history = useHistory();
	const { currentUser } = auth;
	const { userRoles: { isAdmin } } = useUserRoles();

	const { id: routeId } = useParams();
	// The dedicated "/bar/new" route has no :id param, so useParams().id is
	// undefined there. Fall back to "new" (matching the parent BarDetail) so
	// isNew / the "Add Bar" button / new-bar behavior all resolve correctly.
	const id = routeId || "new";
	const stateRegions = useStateRegions();
	const { fetchStateRegions } = useStateRegionActions();
	const isNew = id === "new";
	const stateOptions = useMemo(() => {
		const defaults = [
			{ name: "Alabama", abbr: "AL" },
			{ name: "Alaska", abbr: "AK" },
			{ name: "Arizona", abbr: "AZ" },
			{ name: "Arkansas", abbr: "AR" },
			{ name: "California", abbr: "CA" },
			{ name: "Colorado", abbr: "CO" },
			{ name: "Connecticut", abbr: "CT" },
			{ name: "Delaware", abbr: "DE" },
			{ name: "Florida", abbr: "FL" },
			{ name: "Georgia", abbr: "GA" },
			{ name: "Hawaii", abbr: "HI" },
			{ name: "Idaho", abbr: "ID" },
			{ name: "Illinois", abbr: "IL" },
			{ name: "Indiana", abbr: "IN" },
			{ name: "Iowa", abbr: "IA" },
			{ name: "Kansas", abbr: "KS" },
			{ name: "Kentucky", abbr: "KY" },
			{ name: "Louisiana", abbr: "LA" },
			{ name: "Maine", abbr: "ME" },
			{ name: "Maryland", abbr: "MD" },
			{ name: "Massachusetts", abbr: "MA" },
			{ name: "Michigan", abbr: "MI" },
			{ name: "Minnesota", abbr: "MN" },
			{ name: "Mississippi", abbr: "MS" },
			{ name: "Missouri", abbr: "MO" },
			{ name: "Montana", abbr: "MT" },
			{ name: "Nebraska", abbr: "NE" },
			{ name: "Nevada", abbr: "NV" },
			{ name: "New Hampshire", abbr: "NH" },
			{ name: "New Jersey", abbr: "NJ" },
			{ name: "New Mexico", abbr: "NM" },
			{ name: "New York", abbr: "NY" },
			{ name: "North Carolina", abbr: "NC" },
			{ name: "North Dakota", abbr: "ND" },
			{ name: "Ohio", abbr: "OH" },
			{ name: "Oklahoma", abbr: "OK" },
			{ name: "Oregon", abbr: "OR" },
			{ name: "Pennsylvania", abbr: "PA" },
			{ name: "Rhode Island", abbr: "RI" },
			{ name: "South Carolina", abbr: "SC" },
			{ name: "South Dakota", abbr: "SD" },
			{ name: "Tennessee", abbr: "TN" },
			{ name: "Texas", abbr: "TX" },
			{ name: "Utah", abbr: "UT" },
			{ name: "Vermont", abbr: "VT" },
			{ name: "Virginia", abbr: "VA" },
			{ name: "Washington", abbr: "WA" },
			{ name: "West Virginia", abbr: "WV" },
			{ name: "Wisconsin", abbr: "WI" },
			{ name: "Wyoming", abbr: "WY" }
		];

		const normalized = (stateRegions || []).map((state) => ({
			...state,
			abbr: state?.abbr || "",
			name: state?.name || ""
		}));

		const byKey = new Map(
			normalized.map((state) => [
				String(state.abbr || state.name || state.id).toUpperCase(),
				state
			])
		);

		defaults.forEach((state) => {
			const key = state.abbr.toUpperCase();
			const existing = byKey.get(key) || normalized.find((item) =>
				String(item.name || "").toUpperCase() === state.name.toUpperCase()
			);
			if (!existing) {
				byKey.set(key, {
					id: state.abbr,
					name: state.name,
					abbr: state.abbr,
					regions: []
				});
			}
		});

		return Array.from(byKey.values()).sort((a, b) =>
			String(a.name || a.abbr).localeCompare(String(b.name || b.abbr))
		);
	}, [stateRegions]);

	const { saveBar, markUpdatedForSorting } = useBarUtils();
	const resolveStateIdFromValue = useCallback(
		(value = "") => {
			const normalized = normalizeWhitespace(String(value || "")).toUpperCase();
			if (!normalized) {
				return "";
			}
			const match = (stateOptions || []).find((state) => {
				const abbr = String(state.abbr || "").toUpperCase();
				const name = String(state.name || "").toUpperCase();
				return state.id === value || abbr === normalized || name === normalized;
			});
			return match?.id || "";
		},
		[stateOptions]
	);

	const [openingHours, setOpeningHours] = useState(
		() => createDefaultOpeningHours()
	);

	const [kitchenHours, setKitchenHours] = useState(
		() => createDefaultOpeningHours()
	);

	const [kitchenHoursEnabled, setKitchenHoursEnabled] = useState(false);

	const [barState, barDispatch] = useReducer(
		barInfoReducer,
		createEmptyBarState(),
		barInfoInit
	);

	const getRegions = useCallback(() => {
		fetchStateRegions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const selectedState = useMemo(
		() => stateOptions.find((state) => state.id === barState.stateId),
		[stateOptions, barState.stateId]
	);

	// Re-resolve stateId when stateOptions change (e.g. after async fetch replaces default IDs)
	useEffect(() => {
		if (barState.stateId && !selectedState && barState.state) {
			const resolved = resolveStateIdFromValue(barState.state);
			if (resolved && resolved !== barState.stateId) {
				barDispatch({
					type: "update",
					payload: { name: "stateId", value: resolved }
				});
			}
		}
	}, [stateOptions, selectedState, barState.stateId, barState.state, resolveStateIdFromValue]);

	const selectedRegion = useMemo(
		() => selectedState?.regions?.find((region) => region.id === barState.regionId),
		[selectedState, barState.regionId]
	);
	const { fetchManagers, addManager } = useUserUtils();

	const [hoursHasChanged, setHoursHasChanged] = useState(false);
	const [kitchenHoursHasChanged, setKitchenHoursHasChanged] = useState(false);
	const [addressLookup, setAddressLookup] = useState("");
	const [updateStatusRecord, setUpdateStatusRecord] = useState(null);
	const [completionSaving, setCompletionSaving] = useState(false);
	// Duplicate-check state. `dupWarning` holds the pending bar + the existing
	// same-name bars so the confirmation modal can list them.
	const [dupChecking, setDupChecking] = useState(false);
	const [dupWarning, setDupWarning] = useState(null);
	// Address verification (new bars only). The address must be resolved by
	// Google — via the "Verify Address" button or by picking a search result —
	// before the bar can be saved. `locationMatches` holds any existing bars at
	// the resolved location so we can warn about same-location duplicates.
	const [addressVerified, setAddressVerified] = useState(!isNew);
	const [verifyingAddress, setVerifyingAddress] = useState(false);
	const [verifiedLabel, setVerifiedLabel] = useState("");
	const [locationMatches, setLocationMatches] = useState(null);
	const checkBarDuplicateCallable = useMemo(
		() => functions.httpsCallable("checkBarDuplicate"),
		[]
	);
	const getBarsAtLocationCallable = useMemo(
		() => functions.httpsCallable("getBarsAtLocation"),
		[]
	);

	useEffect(() => {
		onDirtyChange?.(barState.hasChanged || hoursHasChanged || kitchenHoursHasChanged);
	}, [barState.hasChanged, hoursHasChanged, kitchenHoursHasChanged, onDirtyChange]);

	useEffect(() => {
		viewedBar?.id && fetchManagers(viewedBar);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewedBar]);

	useEffect(() => {
		if (!id || isNew || id === "null" || id === "undefined" || !isAdmin) {
			setUpdateStatusRecord(null);
			return undefined;
		}

		const unsubscribe = firestore
			.collection(UPDATED_SORTING_COLLECTION)
			.doc(id)
			.onSnapshot((doc) => {
				if (!doc.exists) {
					setUpdateStatusRecord(null);
					return;
				}
				setUpdateStatusRecord({ id: doc.id, ...(doc.data() || {}) });
			}, (err) => {
				captureError(err, { view: "BarDetail", action: "updateStatusRecord.onSnapshot" });
				console.error("BarDetail updateStatusRecord snapshot error", err);
			});

		return () => unsubscribe?.();
	}, [id, isNew, isAdmin]);

	useEffect(() => {
		setHoursHasChanged(openingHours !== barState.hours);
	}, [openingHours, barState.hours]);

	useEffect(() => {
		const originalEnabled = hoursHaveOpenDay(barState.kitchenHours);
		if (kitchenHoursEnabled !== originalEnabled) {
			setKitchenHoursHasChanged(true);
			return;
		}
		setKitchenHoursHasChanged(
			kitchenHoursEnabled && kitchenHours !== barState.kitchenHours
		);
	}, [kitchenHours, barState.kitchenHours, kitchenHoursEnabled]);

	useEffect(() => {
		getRegions();
		barDispatch({
			type: "reset",
			payload: bar || createEmptyBarState()
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bar]);

	const handleInputChange = (event) => {
		// Editing any location-defining field invalidates a prior verification so
		// the admin must re-verify against Google before saving a new bar.
		if (
			isNew &&
			["barAddress", "city", "state", "postalCode"].includes(event.target.name)
		) {
			setAddressVerified(false);
			setVerifiedLabel("");
		}
		if (event.target.name === "barPhone") {
			const formatted = normalizePhoneNumber(event.target.value);
			barDispatch({
				type: "update",
				payload: {
					name: event.target.name,
					value: formatted
				}
			});
			return;
		}
		barDispatch({
			type: "update",
			payload: {
				name: event.target.name,
				value: event.target.value
			}
		});
	};

	const handleReset = () => {
		barDispatch({
			type: "reset"
		});
	};

	// Look up existing bars within 150m of a coordinate and, if any exist, open
	// the same-location warning modal.
	const runLocationCheck = async (lat, lng) => {
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			return;
		}
		try {
			const { data } = await getBarsAtLocationCallable({
				latitude: lat,
				longitude: lng,
				excludeBarId: isNew ? null : id
			});
			const bars = data?.bars || [];
			if (bars.length > 0) {
				setLocationMatches({ bars, barName: barState.barName });
			}
		} catch (error) {
			captureError(error, { action: "getBarsAtLocation" });
		}
	};

	// Manual "Verify Address" action: geocode the typed address via Google,
	// validate the match actually lands where the admin said (ZIP/city), store
	// the resolved coordinates, and check for existing bars at that location.
	const handleVerifyAddress = async () => {
		const street = normalizeAddressField(barState.barAddress || "");
		const city = normalizeAddressField(barState.city || "");
		if (!street || !city) {
			openNote("Enter at least a street address and city before verifying.");
			return;
		}
		const selectedStateOption = stateOptions.find(
			(state) => state.id === barState.stateId
		);
		const resolvedState = normalizeAddressField(
			normalizeAddressField(barState.state || "") ||
			selectedStateOption?.abbr ||
			selectedStateOption?.name ||
			""
		);
		const enteredZip = normalizeAddressField(barState.postalCode || "");
		const fullAddress = buildFullAddress({
			addressLine: street,
			city,
			state: resolvedState,
			postalCode: enteredZip
		});
		try {
			setVerifyingAddress(true);
			const results = await geocodeByAddress(fullAddress);
			const result = results?.[0] || null;
			const latlng = result ? await getLatLng(result) : null;
			if (!latlng || !Number.isFinite(latlng.lat) || !Number.isFinite(latlng.lng)) {
				openNote(
					"Couldn't verify this address with Google. Check the address and try again."
				);
				return;
			}
			const parts = extractGeocodeParts(result);
			// Reject when Google snapped to a different ZIP than entered — the exact
			// failure behind the 200-mile "Park Shore / US-31" bug.
			if (enteredZip && parts.zip && enteredZip !== parts.zip) {
				openNote(
					`Google matched this to ${parts.formattedAddress || "a different location"} (ZIP ${parts.zip}), not the ZIP you entered (${enteredZip}). Use the address search to pick the exact location.`
				);
				return;
			}
			// With no ZIP to anchor on, fall back to city/state agreement.
			if (
				!enteredZip &&
				parts.city &&
				parts.city.toLowerCase() !== city.toLowerCase()
			) {
				openNote(
					`Google matched this to ${parts.formattedAddress || parts.city}, not ${city}. Add a ZIP or use the address search to pick the exact location.`
				);
				return;
			}
			// A route/approximate match means Google couldn't pinpoint a building.
			if (
				parts.locationType === "APPROXIMATE" ||
				(parts.types || []).includes("route")
			) {
				openNote(
					`Google could only approximate this address (${parts.formattedAddress || fullAddress}). Use the address search to pick an exact match.`
				);
				return;
			}
			barDispatch({
				type: "update",
				payload: { name: "latitude", value: latlng.lat }
			});
			barDispatch({
				type: "update",
				payload: { name: "longitude", value: latlng.lng }
			});
			setVerifiedLabel(parts.formattedAddress || fullAddress);
			setAddressVerified(true);
			await runLocationCheck(latlng.lat, latlng.lng);
		} catch (error) {
			captureError(error, { action: "verifyAddress" });
			openNote(
				"Couldn't verify this address with Google. Check the address and try again."
			);
		} finally {
			setVerifyingAddress(false);
		}
	};

	const handleSave = async (e) => {
		if (e?.preventDefault) e.preventDefault();
		const { ...bar } = barState;

		bar.hours = openingHours;
		bar.kitchenHours = kitchenHoursEnabled
			? kitchenHours
			: createDefaultOpeningHours();
		if (isNew) bar.createdBy = currentUser.uid;
		// Normalize the bar name to Title Case for consistent storage + display.
		// Intentional mixed-case brand names (e.g. "McMenamins", "BJ's") are left
		// untouched — only all-lower / all-upper input is reformatted.
		bar.barName = formatBarName(bar.barName || "");
		const normalizedStreet = normalizeAddressField(bar.barAddress || "");
		const normalizedCity = normalizeAddressField(bar.city || "");
		const normalizedState = normalizeAddressField(bar.state || "");
		const normalizedPostal = normalizeAddressField(bar.postalCode || "");
		const normalizedPhone = normalizePhoneNumber(bar.barPhone || "");
		const selectedStateOption = stateOptions.find((state) => state.id === bar.stateId);
		const resolvedState = normalizeAddressField(
			normalizedState || selectedStateOption?.abbr || selectedStateOption?.name || ""
		);
		bar.address_1 = normalizedStreet;
		bar.barAddress = buildFullAddress({
			addressLine: normalizedStreet,
			city: normalizedCity,
			state: resolvedState,
			postalCode: normalizedPostal
		});
		bar.address_2 = normalizeAddressField(bar.address_2 || "");
		bar.city = normalizedCity;
		bar.state = resolvedState;
		bar.postalCode = normalizedPostal;
		bar.barPhone = normalizedPhone;
		const errorMessage = validateData(bar);
		if (errorMessage) {
			openNote(errorMessage);
			return;
		}

		// New bars must have a Google-verified address before saving.
		if (isNew && !addressVerified) {
			openNote(
				"Please verify the address before adding the bar. Use \"Verify Address\" or pick a result from the address search."
			);
			return;
		}

		// Duplicate prevention (new bars only):
		//  • matching name AND address  -> hard block with an error.
		//  • matching name, different address -> confirm modal (e.g. franchise).
		if (isNew) {
			try {
				setDupChecking(true);
				const { data } = await checkBarDuplicateCallable({
					barName: bar.barName,
					barAddress: bar.barAddress
				});
				const blocked = data?.blocked || null;
				const warnings = data?.warnings || [];
				if (blocked) {
					openNote(
						`"${blocked.name}" is already listed at ${blocked.address || "this address"}. A bar with the same name and address can't be added.`
					);
					return;
				}
				if (warnings.length) {
					// Defer the save until the admin confirms in the modal.
					setDupWarning({ bar, warnings });
					return;
				}
			} catch (error) {
				captureError(error, { action: "checkBarDuplicate" });
				// Fail closed: if we can't verify uniqueness, don't risk creating a
				// duplicate. Surface the problem so the admin can retry.
				openNote(
					"Couldn't verify whether this bar already exists. Please try again."
				);
				return;
			} finally {
				setDupChecking(false);
			}
		}

		await performSave(bar);
	};

	// Actually persists the bar (used directly, and after duplicate confirmation).
	const performSave = async (bar) => {
		// Allow the new-bar redirect performed inside saveBar to bypass the
		// unsaved-changes guard.
		if (savingRef) savingRef.current = true;
		try {
			const result = await saveBar(id, bar, {
				// Always record the update status so the sorting queue is populated
				markUpdateForSorting: true
			});
			const targetId = isNew ? result?.id : id;
			barState.barId = targetId;
			addNewOwner(barState);
			openNote(`Successfully ${isNew ? "created" : "updated"} bar`);
		} catch (error) {
			console.error("Failed to save bar", error);
			captureError(error, { action: "saveBar" });
			openNote("Unable to save bar right now. Please try again.");
		} finally {
			if (savingRef) savingRef.current = false;
		}
	};

	const handleConfirmDuplicate = async () => {
		const pending = dupWarning?.bar;
		setDupWarning(null);
		if (pending) {
			await performSave(pending);
		}
	};

	useEffect(() => {
		if (saveRef) saveRef.current = handleSave;
	});

	const sendResetEmail = async () => {
		if (!barState.ownerEmail) {
			return;
		}

		await firebase
			.auth()
			.sendPasswordResetEmail(barState.ownerEmail);
	};
	const addNewOwner = (barState) => {
		if (!barState.ownerEmail) {
			return;
		}

		addManager({
			email: barState.ownerEmail.toLowerCase(),
			barId: barState.barId,
			barName: barState.barName,
			role: "owner"
		})
			.then(() => {
				sendResetEmail();
			})
			.catch((err) => {
				captureError(err || "addNewOwner failed", { action: "addNewOwner" });
			});
	};

	const handleToggleCompleted = async () => {
		const targetId = barState.barId || id;
		if (!targetId || targetId === "new") {
			return;
		}
		setCompletionSaving(true);
		const nextState = !(updateStatusRecord?.markedAsCompleted);
		try {
			await markUpdatedForSorting(targetId, {
				markedAsCompleted: nextState,
				completedBy: currentUser?.uid || null
			});
			openNote(
				nextState ? "Marked as completed" : "Marked as incomplete"
			);
		} catch (error) {
			console.error("Failed to toggle completion", error);
			captureError(error, { action: "toggleCompletion" });
			openNote("Unable to update completion status right now.");
		} finally {
			setCompletionSaving(false);
		}
	};
	const handleCancel = (e) => {
		e.preventDefault();
		history.replace("/bars");
	};

	const validateData = (bar) => {
		let errorMessages = "";
		// validate barName is required
		if (!bar.barName?.trim()) {
			errorMessages = `${NAME_REQUIRED_TEXT}`;
		}

		if (bar.barPhone && !PHONE_REGEX.test(bar.barPhone)) {
			errorMessages = `${errorMessages}\n${PHONE_NUMBER_VALID_TEXT}`;
		}

		if (bar.barUrl && !WEBSITE_URL_REGEX.test(bar.barUrl)) {
			errorMessages = `${errorMessages}\n${WEBSITE_VALID_TEXT}`;
		}

		// validate position data is required
		let [lat, lon] = [undefined, undefined];

		if (typeof bar.latitude !== "number") {
			lat = bar.latitude?.trim();
			lat = parseFloat(lat);
		} else {
			lat = bar.latitude;
		}

		if (typeof bar.longitude !== "number") {
			lon = bar.longitude?.trim();
			lon = parseFloat(lon);
		} else {
			lon = bar.longitude;
		}

		if (!lat || !lon) {
			errorMessages = `${errorMessages}\n${SELECT_ADDRESS_TEXT}`;
		} else {
			if (isNaN(lat) || isNaN(lon)) {
				errorMessages = `${errorMessages}\n${SELECT_ADDRESS_TEXT}`;
			}
		}
		DAYS.forEach((day) => {
			const dayHours = bar?.hours?.[day];
			if (!dayHours || !dayHours.isOpen) {
				return;
			}

			const readableDay = day.charAt(0).toUpperCase() + day.slice(1);
			const primaryOpen = dayHours.openTime;
			const primaryClose = dayHours.closeTime;

			const hasPrimaryOpen = Boolean(primaryOpen);
			const hasPrimaryClose = Boolean(primaryClose);

			const openMinutes = getTotalMinutes(primaryOpen);
			const closeMinutes = getTotalMinutes(primaryClose);
			const closesNextDay =
				closeMinutes !== null &&
				(openMinutes !== null && closeMinutes < openMinutes || closeMinutes >= MINUTES_IN_DAY);

			if (hasPrimaryOpen && hasPrimaryClose && !isTimeRangeComplete(primaryOpen, primaryClose) && !closesNextDay) {
				errorMessages = `${errorMessages}\n${readableDay}: Closing time must be after opening time.`;
			}
		});

		return errorMessages !== "" ? errorMessages : null; //Return null if there is no error message
	};
	function barInfoInit(rawBarInfo) {
		const base =
			rawBarInfo && typeof rawBarInfo === "object"
				? { ...rawBarInfo }
				: createEmptyBarState();

		if (base.original) {
			delete base.original;
		}

		const hours = buildHoursFromSource(base.hours);
		setOpeningHours(hours);
		const hoursSnapshot = cloneHours(hours);
		const kitchenHoursBuilt = buildHoursFromSource(base.kitchenHours);
		setKitchenHours(kitchenHoursBuilt);
		setKitchenHoursEnabled(hoursHaveOpenDay(kitchenHoursBuilt));
		const kitchenHoursSnapshot = cloneHours(kitchenHoursBuilt);
		const latitude = base?.latitude ?? base?.position?.geopoint?.latitude ?? null;
		const longitude = base?.longitude ?? base?.position?.geopoint?.longitude ?? null;
		const position =
			base?.position?.geopoint
				? base.position
				: latitude !== null && longitude !== null
					? {
						geopoint: {
							latitude,
							longitude
						}
					}
					: base?.position;

		const parsed = base.barAddress ? parseAddressParts(base.barAddress) : null;
		const rawStreet = normalizeAddressField(base.address_1 || parsed?.street || "");
		// Guard: if the street still looks like a composite (contains commas), take only the first segment
		const addressLine = rawStreet.includes(",")
			? normalizeAddressField(rawStreet.split(",")[0])
			: rawStreet;
		const address2 = normalizeAddressField(base.address_2 || "");
		const city = normalizeAddressField(base.city || parsed?.city || "");
		const postalCode = normalizeAddressField(base.zip || base.postalCode || parsed?.postalCode || "");
		const metadataRegion = base?.osmMetadata?.region_id;
		const regionPath = Array.isArray(metadataRegion)
			? metadataRegion
			: typeof metadataRegion === "string"
				? [null, null, metadataRegion]
				: [];
		const metadataState = regionPath[0] || base.state || parsed?.state || "";
		const state = normalizeAddressField(metadataState);
		const resolvedStateId = resolveStateIdFromValue(regionPath[0] || base.stateId || state);
		const resolvedRegionId = regionPath[1] || base.regionId || "";
		const resolvedSubregionId = regionPath[2] || base.subregionId || "";

		const initializedState = {
			...base,
			hours,
			kitchenHours: kitchenHoursBuilt,
			hasChanged: false,
			latitude,
			longitude,
			position,
			barName: toSafeString(base.barName),
			barDescription: toSafeString(base.barDescription),
			barEmail: toSafeString(base.barEmail),
			ownerEmail: toSafeString(base.ownerEmail),
			barPhone: toSafeString(base.barPhone),
			barUrl: toSafeString(base.barUrl),
			barAddress: addressLine,
			address_2: address2,
			city,
			state,
			postalCode,
			stateId: resolvedStateId,
			regionId: resolvedRegionId,
			subregionId: resolvedSubregionId
		};

		return {
			original: {
				...initializedState,
				hours: hoursSnapshot,
				kitchenHours: kitchenHoursSnapshot
			},
			...initializedState
		};
	}

	function barInfoReducer(state, action) {
		const { type, payload } = action;
		let hasChanged = false;
		switch (type) {
			case "reset":
				return barInfoInit(payload || state.original);
			case "update":
				hasChanged = true;
				return {
					...state,
					[payload.name]: payload.value,
					hasChanged
				};
			default:
				return state;
		}
	}

	return (
		<BarInfoContainer>
			<div className="form-section large">
				<TextField
					className="header-like"
					label="Bar Name"
					name="barName"
					fullWidth
					inputProps={{
						style: {
							fontSize: "1.8rem"
						},
						maxLength: 50
					}}
					value={barState.barName}
					onChange={handleInputChange}
					required
				/>

				<TextField
					label="Bar Description"
					name="barDescription"
					fullWidth
					multiline
					value={barState.barDescription}
					onChange={handleInputChange}
					inputProps={{
						maxLength: 500
					}}
				/>
				{/* <TextField
					label="Live Stream URL"
					name="liveUrl"
					fullWidth
					multiline
					value={barState.liveUrl}
					onChange={handleInputChange}
				/> */}
			</div>
			<div className="form-section large basic-info">
				<div className="section-title">Bar Contact</div>
				<div className="two-col-grid">
					<TextField
						label="Bar Email"
						name="barEmail"
						fullWidth
						value={barState.barEmail}
						onChange={handleInputChange}
					/>
					<TextField
						label="Owner Email"
						name="ownerEmail"
						fullWidth
						value={barState.ownerEmail}
						onChange={handleInputChange}
					/>
					<TextField
						label="Bar Phone"
						name="barPhone"
						fullWidth
						value={barState.barPhone}
						onChange={handleInputChange}
						placeholder="123-232-2323"
					/>
					<TextField
						label="Bar Website"
						name="barUrl"
						fullWidth
						value={barState.barUrl}
						onChange={handleInputChange}
						placeholder="barhopperapp.com"
					/>
				</div>
			</div>
			<div className="form-section large address">
				<div className="section-title">Bar Address</div>
				<PlacesAutocomplete
					value={addressLookup}
					searchOptions={{
						componentRestrictions: {
							country: "us"
						}
					}}
					onChange={(value) => setAddressLookup(value)}
					onSelect={async (_address, placeID) => {
						const addressFormat = await geocodeByPlaceId(placeID);
						const formattedAddress = addressFormat?.[0]?.formatted_address || "";
						const parsedAddress = parseAddressParts(formattedAddress);
						const nextStateId = resolveStateIdFromValue(parsedAddress.state || "");
						const latlng = addressFormat?.[0]
							? await getLatLng(addressFormat[0])
							: null;
						setAddressLookup("");
						await Promise.all([
							barDispatch({
								type: "update",
								payload: {
									name: "barAddress",
									value: parsedAddress.street || formattedAddress
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "city",
									value: parsedAddress.city || ""
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "state",
									value: parsedAddress.state || ""
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "stateId",
									value: nextStateId
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "regionId",
									value: ""
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "subregionId",
									value: ""
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "postalCode",
									value: parsedAddress.postalCode || ""
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "latitude",
									value: latlng?.lat ?? barState.latitude
								}
							}),
							barDispatch({
								type: "update",
								payload: {
									name: "longitude",
									value: latlng?.lng ?? barState.longitude
								}
							})
						]);
						// Picking a Google result counts as a verified address.
						if (latlng) {
							setVerifiedLabel(formattedAddress || "");
							setAddressVerified(true);
							await runLocationCheck(latlng.lat, latlng.lng);
						}
					}}
				>
					{({
						getInputProps,
						suggestions,
						getSuggestionItemProps,
						loading
					}) => (
						<div style={{ marginBottom: "16px" }}>
							<TextField
								placeholder="Search for an address..."
								fullWidth
								{...getInputProps({
									label: "Address Lookup",
									className: "location-search-input"
								})}
							/>
							<div className="autocomplete-dropdown-container">
								{loading && (
									<div className="suggestion-item">
										Loading...
									</div>
								)}
								{suggestions.map((suggestion) => {
									const className = suggestion.active
										? "suggestion-item--active"
										: "suggestion-item";
									const style = suggestion.active
										? {
											backgroundColor: "#fafafa",
											cursor: "pointer"
										}
										: {
											backgroundColor: "#ffffff",
											cursor: "pointer"
										};
									return (
										<div
											key={suggestion.id}
											{...getSuggestionItemProps(
												suggestion,
												{
													className,
													style
												}
											)}
										>
											<span>{suggestion.description}</span>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</PlacesAutocomplete>
				<div className="address-grid">
					<TextField
						label="Address"
						name="barAddress"
						fullWidth
						value={barState.barAddress}
						onChange={handleInputChange}
						placeholder="Street address"
						required
					/>
					<TextField
						label="Address 2"
						name="address_2"
						fullWidth
						value={barState.address_2}
						onChange={handleInputChange}
						placeholder="Ste, Unit, Apt, etc. (optional)"
					/>
					<TextField
						label="City"
						name="city"
						fullWidth
						value={barState.city}
						onChange={handleInputChange}
						placeholder="City"
					/>
					<FormControl fullWidth>
						<InputLabel id="state-select-label">State</InputLabel>
						<Select
							labelId="state-select-label"
							label="State"
							value={barState.stateId || ""}
							onChange={(event) => {
								const nextStateId = event.target.value;
								const selectedStateOption = stateOptions.find((state) => state.id === nextStateId);
								if (isNew) {
									setAddressVerified(false);
									setVerifiedLabel("");
								}
								barDispatch({
									type: "update",
									payload: {
										name: "stateId",
										value: nextStateId
									}
								});
								barDispatch({
									type: "update",
									payload: {
										name: "state",
										value: selectedStateOption?.abbr || selectedStateOption?.name || barState.state
									}
								});
								barDispatch({
									type: "update",
									payload: {
										name: "regionId",
										value: ""
									}
								});
								barDispatch({
									type: "update",
									payload: {
										name: "subregionId",
										value: ""
									}
								});
							}}
						>
							<MenuItem value="">-- Select State --</MenuItem>
							{stateOptions.map((state) => (
								<MenuItem key={state.id} value={state.id}>
									{state.name}{state.abbr ? ` (${state.abbr})` : ""}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Zip"
						name="postalCode"
						fullWidth
						value={barState.postalCode}
						onChange={handleInputChange}
						placeholder="Zip"
					/>
				</div>
				{isNew && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "12px",
							marginTop: "12px"
						}}
					>
						<Button
							color="primary"
							variant="outlined"
							onClick={handleVerifyAddress}
							disabled={verifyingAddress}
						>
							{verifyingAddress ? "Verifying…" : "Verify Address"}
						</Button>
						<Typography
							variant="body2"
							color={addressVerified ? "primary" : "textSecondary"}
						>
							{addressVerified
								? `Address verified${verifiedLabel ? `: ${verifiedLabel}` : "."}`
								: "Verify the address before adding this bar."}
						</Typography>
					</div>
				)}
			</div>
			{isAdmin && (
			<div className="form-section large regions">
				<div className="section-title">Bar Regions</div>
				<FormControl fullWidth>
					<InputLabel id="region-select-label">Region</InputLabel>
					<Select
						labelId="region-select-label"
						label="Region"
						value={barState.regionId || ""}
						onChange={(event) => {
							const nextRegionId = event.target.value;
							barDispatch({
								type: "update",
								payload: {
									name: "regionId",
									value: nextRegionId
								}
							});
							barDispatch({
								type: "update",
								payload: {
									name: "subregionId",
									value: ""
								}
							});
						}}
						disabled={!barState.stateId}
					>
						<MenuItem value="">-- Select Region --</MenuItem>
						{(selectedState?.regions || []).map((region) => (
							<MenuItem key={region.id} value={region.id}>
								{region.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{barState.regionId ? (
					<FormControl fullWidth style={{ marginTop: "16px" }}>
						<InputLabel id="subregion-select-label">Subregion</InputLabel>
						<Select
							labelId="subregion-select-label"
							label="Subregion"
							value={barState.subregionId || ""}
							onChange={(event) => {
								barDispatch({
									type: "update",
									payload: {
										name: "subregionId",
										value: event.target.value
									}
								});
							}}
						>
							<MenuItem value="">-- Select Subregion --</MenuItem>
							{(selectedRegion?.subregions || []).map((subregion) => (
								<MenuItem key={subregion.id} value={subregion.id}>
									{subregion.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				) : null}
			</div>
			)}
			<div className="form-section large">
				<label>Open/close date and time</label>
				<OpeningHoursForm
					openingHours={openingHours}
					setOpeningHours={setOpeningHours}
				/>
			</div>
			<div className="form-section large">
				<label>Kitchen hours</label>
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={kitchenHoursEnabled}
							onChange={(e) => setKitchenHoursEnabled(e.target.checked)}
						/>
					}
					label={
						<Typography variant="body2">
							Toggle if kitchen hours are different
						</Typography>
					}
				/>
				{kitchenHoursEnabled && (
					<OpeningHoursForm
						openingHours={kitchenHours}
						setOpeningHours={setKitchenHours}
					/>
				)}
			</div>
			<div className="form-footer">
				<div className="left-actions">
					{!isNew && (
						<ButtonMarkCompleted
							isCompleted={Boolean(updateStatusRecord?.markedAsCompleted)}
							onClick={handleToggleCompleted}
							disabled={completionSaving}
							completedBy={updateStatusRecord?.completedBy}
							completedAt={updateStatusRecord?.completedAt}
							isAdmin={isAdmin}
						/>
					)}
				</div>
				<div className="right-actions">
					{isNew ? (
						<Button
							color="primary"
							variant={"outlined"}
							onClick={handleCancel}
						>
							Cancel
						</Button>
					) : barState.hasChanged || hoursHasChanged || kitchenHoursHasChanged ? (
						<Button
							color="primary"
							variant={"outlined"}
							onClick={handleReset}
						>
							Clear Changes
						</Button>
					) : null}
					<Button
						color="primary"
						variant={
							barState.hasChanged || hoursHasChanged || kitchenHoursHasChanged || isNew
								? "contained"
								: "outlined"
						}
						onClick={handleSave}
						disabled={dupChecking || (isNew && !addressVerified)}
					>
						{dupChecking ? "Checking…" : isNew ? "Add Bar" : "Save"}
					</Button>
				</div>
			</div>

			<Dialog
				open={Boolean(dupWarning)}
				onClose={() => setDupWarning(null)}
			>
				<DialogTitle>Possible duplicate bar</DialogTitle>
				<DialogContent>
					<DialogContentText component="div">
						{`"${dupWarning?.bar?.barName || "This bar"}" is already listed at `}
						{(dupWarning?.warnings || []).map((w) => w.address).filter(Boolean).join("; ")}
						{"."}
						<br />
						<br />
						{`Are you sure you want to add "${dupWarning?.bar?.barName || "this bar"}" at ${dupWarning?.bar?.barAddress || "the new address"}?`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDupWarning(null)}>Cancel</Button>
					<Button color="primary" variant="contained" onClick={handleConfirmDuplicate}>
						Add anyway
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(locationMatches)}
				onClose={() => setLocationMatches(null)}
			>
				<DialogTitle>Other bars at this location</DialogTitle>
				<DialogContent>
					<DialogContentText component="div">
						{`${(locationMatches?.bars || []).length} other bar${
							(locationMatches?.bars || []).length === 1 ? " is" : "s are"
						} already at this location:`}
						<div style={{ margin: "12px 0" }}>
							{(locationMatches?.bars || []).map((b) => (
								<div
									key={b.barId}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: "12px",
										padding: "6px 0"
									}}
								>
									<span>
										{b.name}
										{b.address ? ` — ${b.address}` : ""}
									</span>
									<IconButton
										size="small"
										aria-label={`Edit ${b.name || "bar"}`}
										title="Edit this bar"
										onClick={() => {
											setLocationMatches(null);
											history.push(`/bar/${b.barId}`);
										}}
									>
										<Edit fontSize="small" />
									</IconButton>
								</div>
							))}
						</div>
						{`Are you sure you want to add "${
							locationMatches?.barName || "this bar"
						}" at this location?`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setLocationMatches(null);
							if (isNew) {
								setAddressVerified(false);
							}
						}}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={() => setLocationMatches(null)}
					>
						Yes, add anyway
					</Button>
				</DialogActions>
			</Dialog>
		</BarInfoContainer>
	);
}

const BarInfoContainer = styled.form`
	width: 100%;
	margin: auto;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 8px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	padding: 16px 24px;

	min-height: 100px;
	margin-bottom: 16px;

	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;

	.form-section {
		display: flex;
		flex-direction: column;
		width: 48%;
		margin-bottom: 32px;

		&.large {
			width: 100%;
		}

		.section-title {
			font-size: 0.85rem;
			font-weight: 600;
			color: ${THEME_MAP["color-basic-600"]};
			text-transform: uppercase;
			letter-spacing: 0.08em;
			margin-bottom: 8px;
		}

		.two-col-grid {
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 16px;
		}

		.two-col-grid > div,
		.two-col-grid > .MuiFormControl-root,
		.two-col-grid > .MuiTextField-root {
			margin: 0;
		}

		&.address .address-grid {
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 16px;
		}

		&.address .MuiOutlinedInput-root {
			box-shadow: none !important;
		}

		&.address .MuiInputBase-root {
			box-shadow: none !important;
		}

		&.address .address-grid > div,
		&.address .address-grid > .MuiFormControl-root,
		&.address .address-grid > .MuiTextField-root {
			margin: 0;
		}

		& > div {
			margin: 8px 0;
		}
	}

	.image-uploads {
		background: ${THEME_MAP["color-basic-300"]};
		border-radius: 8px;
		height: 300px;

		display: flex;
		align-items: center;
		justify-content: space-evenly;

		img {
			height: 268px;
			width: auto;
		}
	}

	.form-footer {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;

		.left-actions {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		.right-actions {
			display: flex;
			justify-content: flex-end;
			align-items: center;

			& > button {
				margin-left: 16px;
			}
		}
	}
`;

function ButtonMarkCompleted({ isCompleted, onClick, disabled, completedBy, completedAt, isAdmin }) {
	const [completedByEmail, setCompletedByEmail] = useState(null);

	useEffect(() => {
		if (!isAdmin || !isCompleted || !completedBy) {
			setCompletedByEmail(null);
			return;
		}
		firestore.collection("Users").doc(completedBy).get()
			.then((doc) => setCompletedByEmail(doc.exists ? doc.data()?.email || null : null))
			.catch((err) => {
				setCompletedByEmail(null);
				captureError(err || "fetchCompletedByEmail failed", { action: "fetchCompletedByEmail" });
			});
	}, [isAdmin, isCompleted, completedBy]);

	const formattedDate = completedAt?.toDate
		? completedAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
		: completedAt instanceof Date
			? completedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
			: null;

	return (
		<Box display="flex" alignItems="center" gap={2}>
			<Button
				variant={isCompleted ? "contained" : "outlined"}
				color={isCompleted ? "success" : "primary"}
				onClick={onClick}
				disabled={disabled}
			>
				{isCompleted ? "Mark as Incomplete" : "Mark as Completed"}
			</Button>
			{isAdmin && isCompleted && (completedByEmail || formattedDate) && (
				<Typography variant="body2" color="textSecondary">
					{completedByEmail && <>by {completedByEmail}</>}
					{completedByEmail && formattedDate && " on "}
					{formattedDate}
				</Typography>
			)}
		</Box>
	);
}

function CategorySection({ bar, openNote, onDirtyChange, saveRef }) {
	const categorySnapshots = useCategories();
	const fetchCategories = useFetchCategories();
	const { saveBarCategories, markUpdatedForSorting } = useBarUtils();

	const [categoryDefinitions, setCategoryDefinitions] = useState([]);
	const [selectedSubcategories, setSelectedSubcategories] = useState(
		createEmptyCategoryState()
	);
	const [uniformCategories, setUniformCategories] = useState(true);
	const [activeDay, setActiveDay] = useState(CATEGORY_DAYS[0]);
	const [hasChanged, setHasChanged] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

	useEffect(() => {
		onDirtyChange?.(hasChanged);
	}, [hasChanged, onDirtyChange]);
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

	const [expandedCategories, setExpandedCategories] = useState({});

	const toggleCategoryExpanded = (categoryName) => {
		setExpandedCategories((prev) => ({
			...prev,
			[categoryName]: !prev[categoryName]
		}));
	};

	useEffect(() => {
		if (categorySnapshots.length === 0) {
			fetchCategories();
			return;
		}
		const mapped = [...categorySnapshots]
			.map((definition) => ({
				categoryName: definition.categoryName,
				subcategories: Array.isArray(definition.subcategories)
					? [...definition.subcategories]
					: []
			}))
			.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
		setCategoryDefinitions(mapped);
	}, [categorySnapshots, fetchCategories]);

	const categoriesKey = useMemo(
		() => JSON.stringify(bar?.categories ?? null),
		[bar?.categories]
	);

	const [originalSubcategories, setOriginalSubcategories] = useState(null);

	useEffect(() => {
		const normalized = normalizeCategoryState(bar?.categories);
		const cloned = cloneCategoryState(normalized);
		setSelectedSubcategories(cloneCategoryState(cloned));
		setOriginalSubcategories(cloneCategoryState(cloned));
		setUniformCategories(areDaysUniform(cloned));
		setActiveDay(CATEGORY_DAYS[0]);
		setHasChanged(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoriesKey]);

	const isEveryday = !uniformCategories && activeDay === "everyday";
	const activeDayLabel = activeDay === "everyday" ? "Everyday" : formatDayLabel(activeDay);

	const dayDifferences = useMemo(
		() => getDayDifferences(selectedSubcategories),
		[selectedSubcategories]
	);

	const uniformKeys = useMemo(
		() => getUniformSubcategories(selectedSubcategories),
		[selectedSubcategories]
	);

	const isSubcategoryUniform = (categoryName, subcategory) =>
		uniformKeys.has(`${categoryName}::${subcategory}`);

	const isSubcategoryChecked = (categoryName, subcategory) => {
		if (isEveryday) {
			return CATEGORY_DAYS.every((day) => {
				const selections = selectedSubcategories?.[day]?.[categoryName] || [];
				return selections.includes(subcategory);
			});
		}
		const day = uniformCategories ? CATEGORY_DAYS[0] : activeDay;
		const selections = selectedSubcategories?.[day]?.[categoryName] || [];
		return selections.includes(subcategory);
	};

	const getSelectedCount = (categoryName) => {
		if (isEveryday) {
			const firstDaySelections = selectedSubcategories?.[CATEGORY_DAYS[0]]?.[categoryName] || [];
			return firstDaySelections.filter((sub) =>
				CATEGORY_DAYS.every((day) => {
					const daySelections = selectedSubcategories?.[day]?.[categoryName] || [];
					return daySelections.includes(sub);
				})
			).length;
		}
		const day = uniformCategories ? CATEGORY_DAYS[0] : activeDay;
		return (selectedSubcategories?.[day]?.[categoryName] || []).length;
	};

	const getDayUniqueCount = useCallback((targetDay) => {
		let count = 0;
		const targetData = selectedSubcategories?.[targetDay] || {};
		for (const categoryName of Object.keys(targetData)) {
			for (const sub of targetData[categoryName] || []) {
				const onAllDays = CATEGORY_DAYS.every((d) =>
					(selectedSubcategories?.[d]?.[categoryName] || []).includes(sub)
				);
				if (!onAllDays) count++;
			}
		}
		return count;
	}, [selectedSubcategories]);

	const handleCheckboxToggle = (categoryName, subcategory, checked) => {
		setSelectedSubcategories((prev) => {
			const next = cloneCategoryState(prev);
			const daysToUpdate = (uniformCategories || isEveryday)
				? CATEGORY_DAYS
				: [activeDay];

			daysToUpdate.forEach((day) => {
				const dayData = next[day] || {};
				const currentSelections = Array.isArray(dayData[categoryName])
					? [...dayData[categoryName]]
					: [];

				if (checked) {
					if (!currentSelections.includes(subcategory)) {
						currentSelections.push(subcategory);
						currentSelections.sort();
					}
				} else {
					const index = currentSelections.indexOf(subcategory);
					if (index > -1) {
						currentSelections.splice(index, 1);
					}
				}

				dayData[categoryName] = currentSelections;
				next[day] = dayData;
			});

			const changed = JSON.stringify(next) !== JSON.stringify(originalSubcategories);
			setHasChanged(changed);
			return next;
		});
	};

	const handleUniformToggle = (event) => {
		const checked = event.target.checked;
		if (checked && dayDifferences.hasDifferences) {
			setConfirmDialogOpen(true);
			return;
		}
		applyUniformToggle(checked);
	};

	const applyUniformToggle = (checked) => {
		setUniformCategories(checked);
		setHasChanged(true);
		setSelectedSubcategories((prev) => {
			const next = cloneCategoryState(prev);
			if (checked) {
				const baseDay = activeDay === "everyday" ? CATEGORY_DAYS[0] : activeDay;
				return replicateStateFromDay(next, baseDay);
			}
			setActiveDay("everyday");
			return next;
		});
	};

	const clearAllSelections = () => {
		const empty = {};
		CATEGORY_DAYS.forEach((day) => { empty[day] = {}; });
		setSelectedSubcategories(empty);
		setUniformCategories(true);
		setActiveDay(CATEGORY_DAYS[0]);
		setHasChanged(true);
		setClearConfirmOpen(false);
	};

	const handleSave = async (event) => {
		if (event?.preventDefault) event.preventDefault();
		const effectiveBarId = bar?.barId || bar?.id;
		if (!effectiveBarId) {
			openNote("Save the bar before updating categories.");
			return;
		}
		setSaving(true);
		try {
			const categoriesToSave = uniformCategories
				? replicateStateFromDay(selectedSubcategories, CATEGORY_DAYS[0])
				: cloneCategoryState(selectedSubcategories);

			await saveBarCategories(effectiveBarId, categoriesToSave);
			try {
				await markUpdatedForSorting(effectiveBarId, { resetCompletion: true });
			} catch (sortingError) {
				// Best-effort admin sorting flag; non-admin owners can't write it,
				// so never let that failure fail the category save.
				console.warn("markUpdatedForSorting skipped", sortingError);
			}
			openNote("Categories updated successfully");

			const storedCopy = cloneCategoryState(categoriesToSave);
			setSelectedSubcategories(cloneCategoryState(storedCopy));
			setUniformCategories(areDaysUniform(storedCopy));
			setActiveDay(CATEGORY_DAYS[0]);
			setHasChanged(false);
		} catch (error) {
			console.error("Failed to save categories", error);
			captureError(error, { action: "saveBarCategories" });
			openNote(
				"Unable to update categories right now. Please try again."
			);
		} finally {
			setSaving(false);
		}
	};

	useEffect(() => {
		if (saveRef) saveRef.current = handleSave;
	});

	const isLoading =
		categorySnapshots.length === 0 && categoryDefinitions.length === 0;

	return (
		<CategoryContainer>
			<header className="header">
				<Typography variant="h5" component="h4">
					Category & Services
				</Typography>
			</header>
			{isLoading ? (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress size={32} />
				</Box>
			) : (
				<>
					{categoryDefinitions.length === 0 ? (
						<Typography
							variant="body2"
							color="textSecondary"
						>
							No categories are available yet.
						</Typography>
					) : (
						<>
							<DayControlsBar>
								<FormControlLabel
									control={
										<Switch
											size="small"
											checked={!uniformCategories}
											onChange={(e) => handleUniformToggle({ target: { checked: !e.target.checked } })}
										/>
									}
									label={
										<Typography variant="body2">Categories change throughout the week - e.g. Karaoke Wednesday</Typography>
									}
								/>
								{!uniformCategories && (
									<DayTabsRow>
										<DayTabButton
											type="button"
											onClick={() => setActiveDay("everyday")}
											className={activeDay === "everyday" ? "is-active" : ""}
										>
											Everyday
										</DayTabButton>
										{CATEGORY_DAYS.map((day) => {
											const uniqueCount = getDayUniqueCount(day);
											const label = day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3);
											return (
												<DayTabButton
													key={day}
													type="button"
													onClick={() => setActiveDay(day)}
													className={
														day === activeDay ? "is-active" : ""
													}
												>
													{uniqueCount > 0 ? `${label} (${uniqueCount})` : label}
												</DayTabButton>
											);
										})}
									</DayTabsRow>
								)}
							</DayControlsBar>
							{!uniformCategories && (
								<ActiveDayNotice variant="body2" color="textSecondary">
									{isEveryday
										? "Editing selections that apply to all days. Green Categories indicate everyday selections."
										: `Editing ${activeDayLabel}. Green categories indicate everyday selections.`
									}
								</ActiveDayNotice>
							)}
							<CategoryList>
								{categoryDefinitions.map((definition) => {
									const count = getSelectedCount(definition.categoryName);
									const isExpanded = expandedCategories[definition.categoryName] !== false;
									return (
										<CategoryGroup key={definition.categoryName}>
											<CategoryHeader
												type="button"
												onClick={() => toggleCategoryExpanded(definition.categoryName)}
											>
												<span className="header-left">
													<Typography variant="subtitle1" component="span" fontWeight={600}>
														{definition.categoryName}
													</Typography>
													{count > 0 && (
														<CountBadge>{count}</CountBadge>
													)}
												</span>
												<ExpandMore
													className={`chevron ${isExpanded ? "expanded" : ""}`}
													fontSize="small"
												/>
											</CategoryHeader>
											<Collapse in={isExpanded}>
												<ChipGrid>
													{[...(definition.subcategories || [])]
														.sort((a, b) => a.localeCompare(b))
														.map((subcategory) => {
															const checked = isSubcategoryChecked(
																definition.categoryName,
																subcategory
															);
															const isUniform = !uniformCategories && isSubcategoryUniform(
																definition.categoryName,
																subcategory
															);
															return (
																<Chip
																	key={subcategory}
																	label={subcategory}
																	variant={checked ? "filled" : "outlined"}
																	color={checked ? "primary" : "default"}
																	className={isUniform && checked ? "uniform-chip" : ""}
																	onClick={() => handleCheckboxToggle(
																		definition.categoryName,
																		subcategory,
																		!checked
																	)}
																	size="small"
																/>
															);
														})}
												</ChipGrid>
											</Collapse>
										</CategoryGroup>
									);
								})}
							</CategoryList>
						</>
					)}
				</>
			)}
			<ActionRow>
				<Button
					color="error"
					onClick={() => setClearConfirmOpen(true)}
					disabled={saving}
					sx={{ textTransform: "none" }}
				>
					CLEAR CATEGORIES
				</Button>
				<Button
					color="primary"
					variant={hasChanged ? "contained" : "outlined"}
					onClick={handleSave}
					disabled={saving || !hasChanged}
					endIcon={saving ? <CircularProgress size={18} /> : null}
				>
					Save
				</Button>
			</ActionRow>
			<Dialog
				open={clearConfirmOpen}
				onClose={() => setClearConfirmOpen(false)}
				aria-labelledby="clear-confirm-title"
			>
				<DialogTitle id="clear-confirm-title" sx={{ color: "error.main" }}>
					Clear All Categories?
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This will remove all category selections for every day. You will still need to save for changes to take effect.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setClearConfirmOpen(false)} color="primary">
						Cancel
					</Button>
					<Button
						onClick={clearAllSelections}
						color="error"
						variant="contained"
					>
						Clear
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
				aria-labelledby="uniform-confirm-title"
			>
				<DialogTitle id="uniform-confirm-title" sx={{ color: "warning.main" }}>
					Overwrite Day-Specific Categories?
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						The following categories are only set on specific days. Enabling uniform
						mode will replicate the current day across all days, removing these
						differences.
					</DialogContentText>
					<Box component="ul" sx={{ pl: 2.5, mt: 1.5, mb: 0, fontSize: 13, lineHeight: 1.8 }}>
						{dayDifferences.dayDiffs.map((d, idx) => (
							<li key={idx}>
								<strong>{formatDayLabel(d.day)}</strong> &mdash; {d.category}: {d.subcategories.join(", ")}
							</li>
						))}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDialogOpen(false)} color="primary">
						Go Back
					</Button>
					<Button
						onClick={() => {
							setConfirmDialogOpen(false);
							applyUniformToggle(true);
						}}
						color="warning"
						variant="contained"
					>
						Proceed Anyway
					</Button>
				</DialogActions>
			</Dialog>
		</CategoryContainer>
	);
}

const CategoryContainer = styled.section`
	width: 100%;
	margin: 24px auto;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 8px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	.header {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
`;

const DayControlsBar = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	padding: 8px 12px;
	background: ${THEME_MAP["color-basic-200"]};
	border-radius: 4px;

	.MuiFormControlLabel-root {
		margin-right: 0;
	}
`;

const CategoryList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const CategoryGroup = styled.div`
	border: 1px solid ${THEME_MAP["color-basic-300"]};
	border-radius: 4px;
	overflow: hidden;
`;

const CategoryHeader = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 14px;
	background: ${THEME_MAP["color-basic-200"]};
	border: none;
	cursor: pointer;
	transition: background 0.15s ease;

	&:hover {
		background: ${THEME_MAP["color-basic-300"]};
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.chevron {
		transition: transform 0.2s ease;
		color: ${THEME_MAP["color-basic-600"]};
	}

	.chevron.expanded {
		transform: rotate(180deg);
	}
`;

const CountBadge = styled.span`
	background: ${THEME_MAP["color-primary-500"]};
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	min-width: 20px;
	height: 20px;
	border-radius: 10px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0 6px;
`;

const ChipGrid = styled.div`
	padding: 10px 14px 14px;
	display: flex;
	flex-wrap: wrap;
	gap: 6px;

	.MuiChip-root {
		font-size: 13px;
		border-radius: 4px;
		transition: all 0.15s ease;
		cursor: pointer;
	}

	.MuiChip-outlined {
		border-color: ${THEME_MAP["color-basic-400"]};
		color: ${THEME_MAP["color-basic-700"]};

		&:hover {
			background: ${THEME_MAP["color-basic-200"]};
		}
	}

	.MuiChip-filled.MuiChip-colorPrimary {
		font-weight: 500;
	}

	.uniform-chip {
		background: #2e7d32;
	}
`;

const DayTabsRow = styled.div`
	display: flex;
	gap: 4px;
	flex-wrap: wrap;
`;

const DayTabButton = styled.button`
	padding: 4px 10px;
	border-radius: 4px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	background: ${THEME_MAP["color-basic-100"]};
	color: ${THEME_MAP["color-basic-700"]};
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;

	&.is-active {
		background: ${THEME_MAP["color-primary-500"]};
		color: ${THEME_MAP["color-basic-100"]};
		border-color: ${THEME_MAP["color-primary-500"]};
	}
`;

const ActiveDayNotice = styled(Typography)`
	font-style: italic;
	padding-left: 2px;
`;

const ActionRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

/**
 * promise that resolves to a resized image
 */
const resizeImage = function (settings) {
	const file = settings.file;
	const maxSize = settings.maxSize;
	const reader = new FileReader();
	const image = new Image();
	const canvas = document.createElement("canvas");

	const dataURItoBlob = function (dataURI) {
		const bytes =
			dataURI.split(",")[0].indexOf("base64") >= 0
				? atob(dataURI.split(",")[1])
				: unescape(dataURI.split(",")[1]);
		const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
		const max = bytes.length;
		const ia = new Uint8Array(max);
		for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
		return new Blob([ia], { type: mime });
	};

	const resize = function () {
		let width = image.width;
		let height = image.height;

		if (width > height) {
			if (width > maxSize) {
				height *= maxSize / width;
				width = maxSize;
			}
		} else {
			if (height > maxSize) {
				width *= maxSize / height;
				height = maxSize;
			}
		}

		canvas.width = width;
		canvas.height = height;
		canvas.getContext("2d").drawImage(image, 0, 0, width, height);
		const dataUrl = canvas.toDataURL("image/jpeg");
		return dataURItoBlob(dataUrl);
	};

	return new Promise(function (resolve, reject) {
		// If the file type doesn't match chuck it
		if (!file.type.match(/image.*/)) {
			reject(new Error("Not an image"));
			return;
		}

		reader.onload = function (readerEvent) {
			image.onload = function () {
				return resolve(resize());
			};

			image.src = readerEvent.target.result;
		};

		reader.readAsDataURL(file);
	});
};
